<template>
  <div class="raw-custom-data-config">
    <!-- Colonnes à afficher (toutes sources mélangées : main + relations + meta).
         Les relations sont activées automatiquement dès qu'une colonne ou un
         filtre cible un de leurs champs — pas besoin de toggle séparé. -->
    <div class="cfg-section">
      <label class="cfg-label">
        {{ t('customStats.rawCustomData.columns.label') }}
        <span class="cfg-label-required">*</span>
      </label>
      <div class="cfg-help">
        {{ t('customStats.rawCustomData.columns.help') }}
      </div>
      <MultiSelect
        :model-value="selectedColumns"
        :options="visibleColumnGroups"
        option-group-label="label"
        option-group-children="items"
        option-label="header"
        option-value="field"
        :show-toggle-all="true"
        :max-selected-labels="4"
        :placeholder="t('customStats.rawCustomData.columns.placeholder')"
        class="w-full"
        @update:model-value="onUpdateColumns"
      />
      <div v-if="selectedColumns.length === 0" class="cfg-warning">
        {{ t('customStats.rawCustomData.columns.required') }}
      </div>
    </div>

    <!-- 3. Date column (optionnel) -->
    <div v-if="dateColumnGroups.length > 0" class="cfg-section">
      <label class="cfg-label">
        {{ t('customStats.rawCustomData.dateColumn.label') }}
      </label>
      <div class="cfg-help">
        {{ t('customStats.rawCustomData.dateColumn.help') }}
      </div>
      <Dropdown
        :model-value="selection.dateColumn ?? ''"
        :options="dateColumnGroups"
        option-group-label="label"
        option-group-children="items"
        option-label="header"
        option-value="field"
        :placeholder="t('customStats.rawCustomData.dateColumn.placeholder')"
        :show-clear="true"
        class="w-full"
        @update:model-value="onUpdateDateColumn"
      />
    </div>

    <!-- 4. Filtres user -->
    <div class="cfg-section">
      <label class="cfg-label">
        {{ t('customStats.rawCustomData.filters.label') }}
      </label>
      <div class="cfg-help">
        {{ t('customStats.rawCustomData.filters.help') }}
      </div>
      <div
        v-for="(filter, index) in userFilters"
        :key="index"
        class="cfg-filter-row"
      >
        <Dropdown
          :model-value="filter.field"
          :options="visibleColumnGroups"
          option-group-label="label"
          option-group-children="items"
          option-label="header"
          option-value="field"
          :placeholder="t('customStats.rawCustomData.filters.fieldPlaceholder')"
          class="cfg-filter-field"
          @update:model-value="(v) => onUpdateFilterField(index, v)"
        />
        <Dropdown
          :model-value="filter.operator"
          :options="filterOperators"
          option-label="label"
          option-value="value"
          class="cfg-filter-op"
          @update:model-value="(v) => onUpdateFilterOperator(index, v)"
        />
        <InputText
          v-if="!isUnaryOperator(filter.operator)"
          :model-value="String(filter.value ?? '')"
          class="cfg-filter-value"
          @update:model-value="(v) => onUpdateFilterValue(index, v ?? '')"
        />
        <Button
          icon="far fa-trash"
          class="p-button-text p-button-sm cfg-filter-remove"
          @click="onRemoveFilter(index)"
        />
      </div>
      <Button
        :label="t('customStats.rawCustomData.filters.add')"
        icon="far fa-plus"
        class="p-button-text p-button-sm"
        @click="onAddFilter"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, computed, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

interface AvailableRelation {
  propertyName: string;
  targetSchema: string;
  targetLabel: string;
}
interface AvailableColumn {
  field: string;
  header: string;
  type?: string;
  section: string;
  relationKey?: string;
  isId?: boolean;
}
export interface RawCustomDataMeta {
  idDefinition: number;
  schemaName: string;
  availableRelations: AvailableRelation[];
  availableColumns: AvailableColumn[];
  defaultColumns: string[];
  defaultDateColumn?: string;
}
export interface RawCustomDataFilter {
  field: string;
  operator: string;
  value: string | number | boolean;
}
export interface RawCustomDataSelection {
  selectedColumns: string[];
  enabledRelations: string[];
  userFilters?: RawCustomDataFilter[];
  dateColumn?: string;
}

const UNARY_OPERATORS = new Set(['is_null', 'is_not_null']);

export default defineComponent({
  name: 'RawCustomDataTableWidgetDataConfig',
  components: {
    Dropdown,
    MultiSelect,
    InputText,
    Button,
  },
  props: {
    meta: {
      type: Object as PropType<RawCustomDataMeta>,
      required: true,
    },
    selection: {
      type: Object as PropType<RawCustomDataSelection>,
      required: true,
    },
  },
  emits: ['update:selection'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedColumns = computed(() => props.selection.selectedColumns ?? []);
    const userFilters = computed(() => props.selection.userFilters ?? []);

    // Toutes les colonnes activables sont listées sans pré-condition (la
    // relation correspondante sera JOIN à l'exécution si une colonne ou un
    // filtre la cible — déduction automatique).
    const allColumns = computed(() => props.meta.availableColumns);

    /**
     * Construit les groupes (main / relations m2o / meta) pour un sous-ensemble
     * de colonnes filtrées. Réutilisé par le MultiSelect Colonnes, le Dropdown
     * Date column et le Dropdown Field des filtres user — tous présentent la
     * même hiérarchie pour distinguer la source d'une colonne (notamment pour
     * éviter la confusion sur les colonnes homonymes type `created_at` qui
     * existent à la fois sur la table principale et sur les relations).
     */
    const buildColumnGroups = (predicate: (c: AvailableColumn) => boolean) => {
      const groups: Array<{ label: string; items: AvailableColumn[] }> = [];
      const main = allColumns.value.filter((c) => c.section === 'main' && predicate(c));
      if (main.length > 0) {
        groups.push({ label: t('customStats.rawCustomData.columns.groupMain'), items: main });
      }
      for (const rel of props.meta.availableRelations) {
        const items = allColumns.value.filter(
          (c) => c.relationKey === rel.propertyName && predicate(c),
        );
        if (items.length > 0) {
          groups.push({
            label: t('customStats.rawCustomData.columns.groupRelation', {
              name: rel.targetLabel || rel.propertyName,
            }),
            items,
          });
        }
      }
      const meta = allColumns.value.filter((c) => c.section === 'meta' && predicate(c));
      if (meta.length > 0) {
        groups.push({ label: t('customStats.rawCustomData.columns.groupMeta'), items: meta });
      }
      return groups;
    };

    const visibleColumnGroups = computed(() => buildColumnGroups(() => true));

    const dateColumnGroups = computed(() =>
      buildColumnGroups((c) => c.type === 'datetime' || c.type === 'date'),
    );

    /**
     * `enabledRelations` est auto-déduit : on collecte les `relationKey`
     * uniques des colonnes sélectionnées + des filtres user. Le backend
     * recevra cette liste et ne fera de JOIN que pour les relations
     * réellement utiles.
     */
    const computeEnabledRelations = (
      cols: string[],
      filters: RawCustomDataFilter[],
    ): string[] => {
      const keys = new Set<string>();
      const lookupCol = (path: string) => props.meta.availableColumns
        .find((c) => c.field === path);
      for (const path of cols) {
        const k = lookupCol(path)?.relationKey;
        if (k) keys.add(k);
      }
      for (const f of filters) {
        const k = lookupCol(f.field)?.relationKey;
        if (k) keys.add(k);
      }
      return Array.from(keys);
    };

    const filterOperators = computed(() => [
      { value: 'eq', label: t('customStats.filterOp.eq') },
      { value: 'neq', label: t('customStats.filterOp.neq') },
      { value: 'gt', label: t('customStats.filterOp.gt') },
      { value: 'gte', label: t('customStats.filterOp.gte') },
      { value: 'lt', label: t('customStats.filterOp.lt') },
      { value: 'lte', label: t('customStats.filterOp.lte') },
      { value: 'contains', label: t('customStats.filterOp.contains') },
      { value: 'starts_with', label: t('customStats.filterOp.starts_with') },
      { value: 'ends_with', label: t('customStats.filterOp.ends_with') },
      { value: 'is_null', label: t('customStats.filterOp.is_null') },
      { value: 'is_not_null', label: t('customStats.filterOp.is_not_null') },
    ]);

    const isUnaryOperator = (op: string) => UNARY_OPERATORS.has(op);

    /**
     * Émet la sélection avec `enabledRelations` recalculé automatiquement
     * depuis les colonnes/filtres choisis. Le backend recevra la liste
     * exacte de relations à JOIN — pas de toggle UI à gérer côté user.
     */
    const updateSelection = (patch: Partial<RawCustomDataSelection>) => {
      const next: RawCustomDataSelection = { ...props.selection, ...patch };
      next.enabledRelations = computeEnabledRelations(
        next.selectedColumns ?? [],
        next.userFilters ?? [],
      );
      emit('update:selection', next);
    };

    function onUpdateColumns(values: string[] | null) {
      updateSelection({ selectedColumns: values ?? [] });
    }

    function onUpdateDateColumn(value: string | null) {
      updateSelection({ dateColumn: value || undefined });
    }

    function onAddFilter() {
      const next = [...userFilters.value, { field: '', operator: 'eq', value: '' }];
      updateSelection({ userFilters: next });
    }

    function onRemoveFilter(index: number) {
      const next = userFilters.value.filter((_, i) => i !== index);
      updateSelection({ userFilters: next });
    }

    function onUpdateFilterField(index: number, field: string) {
      const next = userFilters.value.map((f, i) =>
        i === index ? { ...f, field } : f,
      );
      updateSelection({ userFilters: next });
    }

    function onUpdateFilterOperator(index: number, operator: string) {
      const next = userFilters.value.map((f, i) =>
        i === index ? { ...f, operator } : f,
      );
      updateSelection({ userFilters: next });
    }

    function onUpdateFilterValue(index: number, value: string) {
      const next = userFilters.value.map((f, i) =>
        i === index ? { ...f, value } : f,
      );
      updateSelection({ userFilters: next });
    }

    return {
      t,
      selectedColumns,
      userFilters,
      visibleColumnGroups,
      dateColumnGroups,
      filterOperators,
      isUnaryOperator,
      onUpdateColumns,
      onUpdateDateColumn,
      onAddFilter,
      onRemoveFilter,
      onUpdateFilterField,
      onUpdateFilterOperator,
      onUpdateFilterValue,
    };
  },
});
</script>

<style lang="scss" scoped>
.raw-custom-data-config {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cfg-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cfg-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color);
}
.cfg-label-required {
  color: var(--red-500);
  margin-left: 0.25rem;
}
.cfg-label-inline {
  font-size: 0.85rem;
}
.cfg-help {
  font-size: 0.78rem;
  color: var(--text-color-secondary);
  margin-bottom: 0.25rem;
}
.cfg-warning {
  font-size: 0.8rem;
  color: var(--red-500);
}
.cfg-relation-toggles {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface-50, #f8f9fa);
  border-radius: 6px;
}
.cfg-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.cfg-filter-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.4fr auto;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
}
.cfg-filter-field,
.cfg-filter-op {
  width: 100%;
}
.cfg-filter-value {
  width: 100%;
}
.cfg-filter-remove {
  width: 2rem;
  height: 2rem;
}
</style>
